"use client";
import {Separator} from "@/components/ui/separator";
import P from "@/components/typography/p";
import {Button} from "@/components/ui/button";
import LetterGlitch from "@/components/backgrounds/letter-glitch";
import BackendConsole from "@/components/backend-console";
import {useEffect, useState} from "react";
import {cn} from "@/lib/utils";
import {oklchToHex} from "@/lib/oklch";

import type {MouseEvent, TouchEvent} from "react";

const currentYear = new Date().getFullYear();

// Fallback matrix palette, used until the per-load primary hue is read (and if
// the inline hue script didn't run for some reason).
const DEFAULT_GLITCH_COLORS = ["#2b4539", "#61dca3", "#61b3dc"];

export default function Footer() {
    const [open, _setOpen] = useState(false);
    const [openFull, setOpenFull] = useState(false);
    const [glitchColors, setGlitchColors] = useState<string[]>(DEFAULT_GLITCH_COLORS);

    // Tint the matrix to the same random hue as `--primary` (set per page load
    // by the inline script in the root layout). Three lightness steps of that
    // hue give the matrix a dark→bright glow in the page's accent color.
    useEffect(() => {
        const raw = getComputedStyle(document.documentElement)
            .getPropertyValue("--primary-hue")
            .trim();
        const hue = Number(raw);
        if (!raw || Number.isNaN(hue)) return;
        // One-time read of the per-load accent hue from the DOM; deriving the
        // matrix colours from it necessarily happens after mount.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setGlitchColors([
            oklchToHex(0.30, 0.08, hue),
            oklchToHex(0.62, 0.163, hue),
            oklchToHex(0.80, 0.15, hue),
        ]);
    }, []);

    const setOpen = (val: boolean, _e: MouseEvent|TouchEvent) => {
        _setOpen(val);
    }

    return (
        <footer
            data-open={open}
            style={{
                // Use dvh (dynamic viewport) not vh: on iOS Safari `vh` is the
                // large viewport (URL bar hidden), so when the bar reappears the
                // bottom-anchored footer's top is pushed above the visible area
                // and gets clipped. dvh tracks the actual visible height.
                transform: openFull ? '' : 'translateY(calc(100dvh - 78px))'
            }}
            className={cn("main-footer fixed bottom-0 left-0 right-0 h-[100dvh] w-screen bg-background translate-y-14 transition ease-in duration-200 data-[open=true]:translate-y-0",
                openFull ? 'translate-y-15' : 'hover:translate-y-0'
            )}
            onMouseLeave={(e) => setOpen(false, e)}
        >
            <div className="animate-bounce-once">
                <Separator/>
                <div
                    onTouchStart={(e) => {
                        if (!openFull) {
                            setOpen(true, e)
                        }
                    }}
                    onMouseOver={(e) => {
                        if (!openFull) {
                            setOpen(true, e)
                        }
                    }}
                >
                    <Separator orientation="vertical"/>
                    <P className="text-center">Josh Wood &copy; {currentYear}</P>
                </div>
                <div className="h-[100dvh] contain-content flex justify-center items-start">
                    <div className="flex items-center justify-center text-xl h-14">
                        <Button
                            variant="outline"
                            className="border-primary/50 bg-black/40 font-mono font-bold uppercase tracking-[0.25em] text-primary shadow-[0_0_30px_-10px_var(--primary)] backdrop-blur-sm hover:bg-primary/10 hover:text-primary"
                            onClick={(e) => {
                                setOpenFull(!openFull)
                                if (!openFull) {
                                    setOpen(false, e)
                                }
                            }}
                        >
                            {openFull ? "RETURN" : "THE BACKEND?"}
                        </Button>
                    </div>
                    {(open || openFull) && <LetterGlitch glitchColors={glitchColors} className="h-[100dvh] absolute inset-0 -z-1"/>}
                    <BackendConsole active={openFull} onExit={() => setOpenFull(false)}/>
                </div>
            </div>
        </footer>
    )
}